import forEach from 'lodash/forEach';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useConfig } from '../../hooks';
import { useStores } from '../../stores';
import { Controls } from './Controls';
import renderLanguages from './renderLanguages';

const CloseButton = React.lazy(() => import('./CloseButton'));
const MinimizeButton = React.lazy(() => import('./MinimizeButton'));
const LiveChatButton = React.lazy(() => import('./LiveChatButton'));
const FullScreenButton = React.lazy(() => import('./FullScreenButton'));
const SurveyButton = React.lazy(() => import('./SurveyButton'));
const MainMenu = React.lazy(() => import('./MainMenu'));
const LanguagesMenu = React.lazy(() => import('./LanguagesMenu'));

const DesktopControls = (props, ref) => {
  const {
    apiURL,
    header: { desktopControls, controls }
  } = useConfig();
  const { chatWindow, app } = useStores();
  const { i18n } = useTranslation();
  const [filteredDesktopControls, setFilteredDesktopControls] = React.useState([]);
  const configControls = controls || desktopControls;
  const Components = React.useMemo(
    () => ({
      mainMenu: MainMenu,
      languages: LanguagesMenu,
      liveChat: LiveChatButton,
      survey: SurveyButton,
      ...(chatWindow.isPopIn && {
        fullscreen: FullScreenButton,
        minimize: MinimizeButton,
        close: CloseButton
      })
    }),
    [chatWindow.isPopIn]
  );

  const renderControlItem = React.useCallback(
    ({ name, items = [] }, idx) => {
      const Component = Components[name];

      if (Component) {
        return <Component key={idx} items={items} className="react-rnd-draggable-cancel" />;
      }

      return null;
    },
    [Components]
  );

  React.useEffect(() => {
    let filterLanguages;
    forEach(configControls, control => {
      if (control.name === 'languages') {
        filterLanguages = control;
      }
    });
    if (!filterLanguages.enabled) {
      const getDefaultLanguage = filterLanguages.items.filter(control => control.enabled);
      if (getDefaultLanguage.length) {
        i18n.changeLanguage(getDefaultLanguage[0].name);
        app.setIsChangeLanguage(true, getDefaultLanguage[0].name);
      }
    }

    const filteredControls = configControls.filter(control => control.enabled);
    forEach(filteredControls, control => {
      if (control.items && control.items.length) {
        const filteredItems = control.items.filter(item => item.enabled);
        if (control.name === 'languages') {
          const filteredItemsLanguages = renderLanguages(filteredItems, apiURL, control.default);
          control.items = filteredItemsLanguages;
        } else {
          control.items = filteredItems;
        }
        return control.items;
      }

      return null;
    });
    setFilteredDesktopControls(filteredControls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Controls ref={ref}>
      <React.Suspense fallback={null}>
        {filteredDesktopControls.length
          ? filteredDesktopControls.sort((a, b) => a.position - b.position).map(renderControlItem)
          : configControls.sort((a, b) => a.position - b.position).map(renderControlItem)}
      </React.Suspense>
    </Controls>
  );
};

export default observer(DesktopControls, { forwardRef: true });
